import { useEffect, useState } from "react";
import { api, formatApiError, downloadPdf } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Download, ShieldCheck, CalendarDays, FileText, Receipt, Vault } from "lucide-react";
import { toast } from "sonner";

const KIND_META = {
  leave: { label: "Congé", Icon: CalendarDays, cls: "bg-[#0066FF]/10 text-[#0066FF]" },
  certificate: { label: "Attestation de travail", Icon: FileText, cls: "bg-emerald-100 text-emerald-700" },
  payslip: { label: "Bulletin de paie", Icon: Receipt, cls: "bg-amber-100 text-amber-700" },
};

function fmt(iso) {
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso || "—"; }
}

export default function EmployeeVault() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/vault"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const dl = async (item) => {
    try { await downloadPdf(item.download_url, item.filename); toast.success("Téléchargement démarré"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = tab === "all" ? items : items.filter((i) => i.kind === tab);
  const counts = {
    all: items.length,
    leave: items.filter((i) => i.kind === "leave").length,
    certificate: items.filter((i) => i.kind === "certificate").length,
    payslip: items.filter((i) => i.kind === "payslip").length,
  };

  return (
    <div>
      <PageHeader
        title="Mon Coffre-fort Numérique"
        subtitle="Retrouvez ici l'intégralité de vos documents validés — attestations de congé, attestations de travail et bulletins de paie."
      />

      <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 border-slate-200 flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-slate-900 text-white flex items-center justify-center"><Vault className="h-5 w-5" /></div>
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Documents</div>
            <div className="font-heading text-2xl font-bold text-slate-900">{counts.all}</div>
          </div>
        </Card>
        {Object.entries(KIND_META).map(([k, m]) => {
          const Icon = m.Icon;
          return (
            <Card key={k} className="p-4 border-slate-200 flex items-center gap-3">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${m.cls}`}><Icon className="h-5 w-5" /></div>
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{m.label}</div>
                <div className="font-heading text-2xl font-bold text-slate-900">{counts[k]}</div>
              </div>
            </Card>
          );
        })}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="vault-tabs">
          <TabsTrigger value="all" data-testid="vault-tab-all">Tous ({counts.all})</TabsTrigger>
          <TabsTrigger value="leave" data-testid="vault-tab-leave">Congés ({counts.leave})</TabsTrigger>
          <TabsTrigger value="certificate" data-testid="vault-tab-cert">Attestations ({counts.certificate})</TabsTrigger>
          <TabsTrigger value="payslip" data-testid="vault-tab-payslip">Bulletins ({counts.payslip})</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          {loading ? (
            <Card className="p-8 border-slate-200 text-center text-sm text-slate-500">Chargement…</Card>
          ) : filtered.length === 0 ? (
            <Card className="p-12 border-slate-200 text-center">
              <ShieldCheck className="h-10 w-10 text-slate-300 mx-auto" />
              <p className="mt-3 text-sm text-slate-500">Aucun document disponible dans cette catégorie</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filtered.map((it) => {
                const meta = KIND_META[it.kind] || KIND_META.certificate;
                const Icon = meta.Icon;
                return (
                  <Card key={`${it.kind}-${it.id}`} className="p-5 border-slate-200 hover:shadow-md transition-shadow" data-testid={`vault-item-${it.id}`}>
                    <div className="flex items-start justify-between">
                      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${meta.cls}`}><Icon className="h-5 w-5" /></div>
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">Validé</span>
                    </div>
                    <div className="mt-4">
                      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{meta.label}</div>
                      <div className="font-heading font-semibold text-slate-900 mt-1 text-base leading-tight">{it.title}</div>
                      <div className="text-sm text-slate-600 mt-1 truncate">{it.subtitle}</div>
                      <div className="text-xs text-slate-400 mt-1">Émis le {fmt(it.date)}</div>
                    </div>
                    <Button
                      data-testid={`vault-download-${it.id}`}
                      onClick={() => dl(it)}
                      className="mt-5 w-full bg-[#0066FF] hover:bg-[#0052CC]"
                    >
                      <Download className="h-4 w-4 mr-2" />Télécharger le PDF
                    </Button>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
